import { Ref, ref, watch, unref, reactive, readonly } from '@vue/composition-api';
import { notNil } from '@berlin-front/common/utils';

/**
 * 進捗関数。
 * @param time - 進捗バーがスタートしてからの経過時間。
 * @returns 進捗率。0 -> 1まで進捗する。
 */
type FakeProgressFn = (time: number) => number;

/**
 * 未完了時の最大進捗。
 */
const NO_COMPLETED_MAX_PROGRESS = 0.99;

/**
 * 一つのフェイク進捗関数の最小持続時間。
 */
const FAKE_PROGRESS_MIN_DURATION = 300;

/**
 * 終了アニメーションのスピード。
 * 200msで進捗1進む。
 */
const STEP2_SPEED = 1 / 700;

/**
 * 何か実際に処理をしていることを期待させる進捗関数を返します
 *
 * @param expectedDuration 期待される実行時間ミリ秒。
 * @returns 進捗関数。作成時のexpectedDurationを引数としたときに、丁度進捗率が1となるよう調整されている。
 */
const createFakeProgress = (expectedDuration: number): FakeProgressFn => {
  const subFunctions: {
    offsetProgress: number;
    startTime: number;
    speed: number;
  }[] = [];
  let totalProgress = 0;
  let totalTime = 0;
  let isFirst = true;
  // - 引数で受け取った時間範囲を、ランダムな複数の時間範囲に分割する。
  // - 分割した時間間隔それぞれにランダムなスピードの進捗関数を設定する。
  // 一つ一つの進捗関数は等速で進む。異なるスピードの進捗関数を組み合わせることで、
  // 重い処理とか軽い処理とかの存在を匂わせ、リアル感をだす。
  while (totalTime < expectedDuration) {
    // 関数の持続時間をランダムで決定する。
    const dt = Math.min(
      expectedDuration - totalTime,
      [1, 2, 3][Math.floor(Math.random() * 3)] * FAKE_PROGRESS_MIN_DURATION,
    );
    // 初回は最大値。期待感を持たせる。ランダムのバラツキはフィボナッチ。
    const speed = isFirst ? 21 : [1, 2, 3, 5, 8, 13, 21][Math.floor(Math.random() * 7)];
    isFirst = false;

    const recode = {
      offsetProgress: totalProgress,
      startTime: totalTime,
      speed,
    };
    subFunctions.unshift(recode);
    totalProgress += speed * dt;
    totalTime += dt;
  }
  return (time: number): number => {
    const rec = notNil(subFunctions.find((recode) => recode.startTime <= time));
    const progress = (rec.offsetProgress + rec.speed * (time - rec.startTime)) / totalProgress;
    return progress;
  };
};

/**
 * フェイク進捗composable。
 *
 * 実はないがそれらしい進捗処理率を示す、composable。
 * @param expectedDuration - 進捗終了までの予測時間。
 * @param isLoading - trueに設定された時、進捗アニメーションが開始するリアクティブパラメータ。
 * @param completed - trueに設定された時、完了アニメーションが開始するリアクティブパラメータ。
 * @returns {{ progress: Ref<number> completed: Ref<boolean> }} progressは進捗率、 completedは進捗終了しているかどうかを示す。
 */
const useFakeProgress = (expectedDuration: Ref<number> | number, isLoading: Ref<boolean>, completed: Ref<boolean>) => {
  /* 進捗割合。 */
  const progressRatio = ref(0);
  const loadingState = ref(false);
  const progressCompleteState = ref(false);

  //
  // step1アニメーションに使用する変数。
  //
  /* 進捗バー開始時刻 */
  let step1StartTime = NaN;
  /* 進捗関数 */
  let fakeProgress: FakeProgressFn | undefined;

  //
  // step2アニメーションに使用する変数。
  //
  /* ロード終了処理開始時刻のprogress。終了処理のアニメーションの中割りに使用する。 */
  let step2StartProgress = NaN;
  /* 終了処理開始時刻 */
  let step2StartTime = NaN;

  /**
   * step1はランダム進捗ステップ。ローディング処理が完了するまでは、fakeの進捗状況を提供する。
   */
  const step1 = (time: number) => {
    // ローディング中では無くなるか、完了状態になるか、進捗が最大に達したらstep1は終了とする。
    if (!unref(isLoading) || unref(completed) || progressRatio.value >= NO_COMPLETED_MAX_PROGRESS) {
      return;
    }
    // 進捗スタート時の状態記録と、進捗関数生成。
    if (Number.isNaN(step1StartTime)) {
      loadingState.value = true;
      progressCompleteState.value = false;
      step1StartTime = time;
      fakeProgress = createFakeProgress(unref(expectedDuration));
    }
    // 各ステップでは、スタート時に作成した進捗関数を使用し、進捗を進める。この時、進捗最大値を超えないようにする。
    if (!completed.value) {
      const elapsedTime = time - step1StartTime;
      progressRatio.value = Math.min(notNil(fakeProgress)(elapsedTime), NO_COMPLETED_MAX_PROGRESS);
      requestAnimationFrame(step1);
    }
  };

  /**
   * step2は終了ステップ。
   * ローディングの終了を検知し、一気に進捗処理を完了させる。
   * この時のスピードは等速。
   */
  const step2 = (time: number) => {
    // completedでなくなったら強制的にアニメーション終了する。
    if (!unref(completed)) {
      return;
    }
    if (Number.isNaN(step2StartTime)) {
      // 開始時の時刻と進捗を記録。step2の初回実行は進捗0になるけど、依存関係少なくてわかりやすくするためにあえてそうしてる。
      step2StartTime = time;
      step2StartProgress = progressRatio.value;
    }
    // 時間経過に合わせ進捗を設定する。
    const elapsedTime = time - step2StartTime;
    progressRatio.value = Math.min(1, step2StartProgress + STEP2_SPEED * elapsedTime);
    // 進捗率が1に達するまでアニメーションを続ける。
    if (progressRatio.value < 1) {
      requestAnimationFrame(step2);
    }
    if (progressRatio.value === 1) {
      loadingState.value = false;
      progressCompleteState.value = true;
      progressRatio.value = 0;
    }
  };
  /**
   * isLoadingがtrueになるのをトリガーにして、アニメーションを開始する。
   */
  watch(
    [isLoading],
    () => {
      if (unref(isLoading)) {
        fakeProgress = undefined;
        progressRatio.value = 0;
        progressCompleteState.value = false;
        step1StartTime = NaN;
        requestAnimationFrame(step1);
      }
    },
    {
      immediate: true,
    },
  );
  /**
   * completedをトリガーにして、step2終了アニメーションを開始する。
   */
  watch(
    [completed],
    () => {
      if (unref(completed)) {
        step2StartProgress = NaN;
        step2StartTime = NaN;
        requestAnimationFrame(step2);
      }
    },
    {
      immediate: true,
    },
  );
  return readonly(
    reactive({
      progress: progressRatio,
      completed: progressCompleteState,
      isLoading: loadingState,
    }),
  );
};

export default useFakeProgress;
